import type {
  DoubaoTtsProviderConfigView,
  UpdateDoubaoTtsProviderConfigInput,
} from "@actale/contracts";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api, ApiError } from "./api.js";
import { formatUpdatedAt } from "./story-utils.js";

const TTS_2_RESOURCE_ID = "seed-tts-2.0";
const TTS_2_MIA_SPEAKER_ID = "zh_female_vv_uranus_bigtts";
const TTS_1_RESOURCE_ID = "seed-tts-1.0";
const TTS_1_MIA_SPEAKER_ID = "zh_female_shuangkuaisisi_emo_v2_mars_bigtts";

interface DoubaoTtsForm {
  apiKey: string;
  resourceId: string;
  speakerId: string;
  emotion: string;
  emotionScale: number;
}

function formFrom(config: DoubaoTtsProviderConfigView): DoubaoTtsForm {
  return {
    apiKey: "",
    resourceId: config.resourceId,
    speakerId: config.speakerId,
    emotion: config.emotion,
    emotionScale: config.emotionScale,
  };
}

function message(caught: unknown, fallback: string): string {
  return caught instanceof ApiError ? caught.message : fallback;
}

export function DoubaoTtsProviderConfigPanel({ refreshToken = 0 }: { refreshToken?: number }): React.ReactNode {
  const [config, setConfig] = useState<DoubaoTtsProviderConfigView | null>(null);
  const [form, setForm] = useState<DoubaoTtsForm | null>(null);
  const [showKey, setShowKey] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await api.doubaoTtsProviderConfig();
      setConfig(result);
      setForm(formFrom(result));
      setShowKey(false);
      setDeleteConfirm(false);
    } catch (caught) {
      setError(message(caught, "无法读取豆包语音合成配置"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load, refreshToken]);

  async function save(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!config || !form) return;
    if (!config.configured && !form.apiKey.trim()) {
      setError("首次配置必须填写 API Key");
      return;
    }
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const input: UpdateDoubaoTtsProviderConfigInput = {
        resourceId: form.resourceId === TTS_1_RESOURCE_ID ? TTS_1_RESOURCE_ID : TTS_2_RESOURCE_ID,
        speakerId: form.speakerId.trim(),
        emotion: form.emotion.trim(),
        emotionScale: form.emotionScale,
        expectedUpdatedAt: config.updatedAt,
        ...(form.apiKey.trim() ? { apiKey: form.apiKey.trim() } : {}),
      };
      const result = await api.updateDoubaoTtsProviderConfig(input);
      setConfig(result);
      setForm(formFrom(result));
      setShowKey(false);
      setDeleteConfirm(false);
      setNotice("豆包语音合成配置已加密保存；本次保存没有调用模型，也没有产生费用。");
    } catch (caught) {
      setError(message(caught, "豆包语音合成配置保存失败"));
    } finally {
      setSaving(false);
    }
  }

  async function remove(): Promise<void> {
    if (!config?.updatedAt || !deleteConfirm) return;
    setDeleting(true);
    setError("");
    setNotice("");
    try {
      await api.deleteDoubaoTtsProviderConfig(config.updatedAt);
      await load();
      setNotice("豆包语音合成配置密文和默认参数已删除；已有音频不会被删除。");
    } catch (caught) {
      setError(message(caught, "删除豆包语音合成配置失败"));
    } finally {
      setDeleting(false);
    }
  }

  if (loading && !config) {
    return <div className="director-loading"><span /><strong>正在读取豆包语音合成配置…</strong></div>;
  }

  if (!config || !form) {
    return <div className="state-card"><div className="error-notice" role="alert"><span>!</span>{error || "豆包语音合成配置不可用"}</div><button className="secondary-button" onClick={() => void load()}>重新加载</button></div>;
  }

  const busy = loading || saving || deleting;
  const isTts2 = form.resourceId === TTS_2_RESOURCE_ID;

  function selectResource(resourceId: string): void {
    setForm({
      ...form!,
      resourceId,
      speakerId: resourceId === TTS_2_RESOURCE_ID ? TTS_2_MIA_SPEAKER_ID : TTS_1_MIA_SPEAKER_ID,
      emotion: resourceId === TTS_2_RESOURCE_ID ? "auto" : "sad",
      emotionScale: resourceId === TTS_2_RESOURCE_ID ? 1 : 2,
    });
  }

  return (
    <form className="provider-config-form provider-tts-card" onSubmit={(event) => void save(event)}>
      <header>
        <div>
          <span className={`provider-state-dot ${config.configured ? "ready" : "empty"}`} />
          <div><strong>豆包语音合成</strong><small>{config.provider} · {config.resourceId}</small></div>
        </div>
        <span className={`provider-config-status ${config.configured ? "ready" : "empty"}`}>{config.configured ? "已配置" : "未配置"}</span>
      </header>

      {error ? <div className="error-notice provider-inline-notice" role="alert"><span>!</span>{error}</div> : null}
      {notice ? <div className="director-notice provider-inline-notice" role="status">✓ {notice}</div> : null}
      {!config.encryptionReady ? (
        <div className="provider-security-alert provider-inline-notice" role="alert">
          <strong>服务器加密主密钥尚未就绪</strong>
          <span>在服务器配置主密钥并重启 API 后，才能保存豆包语音合成 API Key。</span>
        </div>
      ) : null}

      <div className="provider-form-grid">
        <label>
          <span>语音模型</span>
          <select value={form.resourceId} onChange={(event) => selectResource(event.target.value)} required>
            <option value={TTS_2_RESOURCE_ID}>豆包语音合成模型 2.0（推荐）</option>
            <option value={TTS_1_RESOURCE_ID}>豆包语音合成模型 1.0（旧配置）</option>
          </select>
          <small>切换模型会填入对应的预设音色和演绎参数，保存前请核对。</small>
        </label>
        <label>
          <span>演绎方式</span>
          {isTts2 ? <input value="2.0 根据台词语义自动演绎" disabled readOnly /> : <input value={form.emotion} onChange={(event) => setForm({ ...form, emotion: event.target.value })} maxLength={40} placeholder="sad" pattern="[a-z][a-z_]*" required />}
          <small>{isTts2 ? "根据台词自动演绎，无需设置固定情绪。" : "填写该音色支持的情绪，例如 sad（伤心）。"}</small>
        </label>
        <label className="provider-wide-field">
          <span>默认音色 ID</span>
          <input value={form.speakerId} onChange={(event) => setForm({ ...form, speakerId: event.target.value })} maxLength={160} placeholder="供应商提供的音色 ID" required />
          <small>用于语音生成的默认音色，不会自动覆盖故事角色配置。</small>
        </label>
        {isTts2 ? null : <label>
          <span>情绪强度（1—5）</span>
          <input type="number" min="1" max="5" step="1" value={form.emotionScale} onChange={(event) => setForm({ ...form, emotionScale: Number(event.target.value) })} required />
        </label>}
        <label>
          <span>输出规格</span>
          <input value={`${config.audioFormat.toUpperCase()} · ${config.sampleRate / 1000} kHz`} disabled readOnly />
        </label>
        <label className="provider-wide-field">
          <span>固定接口</span>
          <input type="url" value={config.apiBaseUrl} disabled readOnly />
          <small>接口由系统固定，后台不能改成其他地址。</small>
        </label>
        <label className="provider-wide-field">
          <span>{config.configured ? "API Key（留空表示不更换）" : "API Key"}</span>
          <div className="provider-secret-input">
            <input type={showKey ? "text" : "password"} value={form.apiKey} onChange={(event) => setForm({ ...form, apiKey: event.target.value })} autoComplete="new-password" minLength={8} maxLength={8192} placeholder={config.configured ? `已保存 ${config.apiKeyHint ?? "加密密钥"}` : "输入火山引擎语音合成 API Key"} required={!config.configured} />
            <button type="button" className="text-button" onClick={() => setShowKey(!showKey)}>{showKey ? "隐藏" : "显示"}</button>
          </div>
          <small>保存后页面和接口都不会再次获得或显示密钥明文。</small>
        </label>
      </div>

      <div className="provider-save-warning">
        <span aria-hidden="true">!</span>
        <p><strong>这里只保存配置，不会调用模型或产生费用</strong><small>保存配置不会生成语音或覆盖故事角色配置。</small></p>
      </div>

      <footer>
        <span>{config.updatedAt ? `最近更新：${formatUpdatedAt(config.updatedAt)} · ${config.updatedBy?.displayName ?? "系统"}` : "尚无已保存配置"}</span>
        <button className="primary-button compact" disabled={busy || !config.encryptionReady || (!config.configured && !form.apiKey.trim())}>{saving ? "正在加密保存…" : "加密保存配置"}</button>
      </footer>

      {config.configured ? (
        <div className="provider-delete-zone">
          <strong>删除配置</strong>
          <small>只删除数据库中的密文和默认参数，不会删除已经生成或上传的音频。</small>
          {!deleteConfirm ? <button type="button" className="danger-text-button" onClick={() => setDeleteConfirm(true)}>删除豆包语音合成配置</button> : (
            <div><button type="button" className="danger-button" disabled={busy} onClick={() => void remove()}>{deleting ? "正在删除…" : "确认删除"}</button><button type="button" className="text-button" onClick={() => setDeleteConfirm(false)}>取消</button></div>
          )}
        </div>
      ) : null}
    </form>
  );
}
